import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  ArrowLeft, 
  Dumbbell, 
  Target, 
  Settings, 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Calendar,
  Layers,
  Info,
  Edit,
  Play,
  FileText,
  Activity
} from "lucide-react";
import { exercisesAPI } from "../services/api";
import { useTheme } from "../contexts/ThemeContext";
import { useApp } from "../contexts/AppContext";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ExerciseModal from "../components/ui/ExerciseModal";

const ExerciseDetailsPage = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { selectedOrganizationId, isSuperAdmin } = useApp();
  
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const fetchExercise = useCallback(async () => {
    try {
      setLoading(true);
      const response = await exercisesAPI.getById(selectedOrganizationId, exerciseId);
      setExercise(response.data.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching exercise details:", err);
      setError("Egzersiz detayları yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizationId, exerciseId]);

  useEffect(() => {
    if (selectedOrganizationId && exerciseId) {
      fetchExercise();
    }
  }, [selectedOrganizationId, exerciseId, fetchExercise]);

  const handleUpdate = async (updatedData) => {
    try {
      const response = await exercisesAPI.update(selectedOrganizationId, exerciseId, updatedData);
      setExercise(response.data.data);
      setIsEditModalOpen(false);
    } catch (err) {
      console.error("Error updating exercise:", err);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      approved: {
        bg: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
        icon: CheckCircle,
        label: t("exercisesPage.status.approved")
      },
      rejected: {
        bg: "bg-rose-500/10 text-rose-500 border-rose-500/20",
        icon: XCircle,
        label: t("exercisesPage.status.rejected")
      },
      pending: {
        bg: "bg-amber-500/10 text-amber-500 border-amber-500/20",
        icon: Clock,
        label: t("exercisesPage.status.pending")
      }
    };

    const s = config[status] || config.pending;
    const Icon = s.icon;

    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${s.bg}`}>
        <Icon className="w-3.5 h-3.5 mr-1.5" />
        {s.label}
      </div>
    );
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner": return "text-emerald-500";
      case "intermediate": return "text-amber-500";
      case "advanced": return "text-rose-500";
      default: return "text-blue-500";
    }
  };

  if (loading) {
    return (
      <div className={`flex h-full items-center justify-center ${colors.bgPrimary}`}>
        <LoadingSpinner size="large" message={t("organizationDetailsPage.loading")} />
      </div>
    );
  }

  if (error || !exercise) {
    return (
      <div className={`flex flex-col h-full items-center justify-center ${colors.bgPrimary} p-6`}>
        <div className={`p-8 bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border ${colors.cardBorder} text-center max-w-md`}>
          <XCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
          <h2 className={`text-xl font-bold ${colors.textPrimary} mb-2`}>{t("exerciseDetails.errorTitle")}</h2>
          <p className={`${colors.textTertiary} mb-6`}>{error || t("exerciseDetails.notFound")}</p>
          <button 
            onClick={() => navigate("/exercises")}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            {t("exerciseDetails.backToList")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${colors.bgPrimary} overflow-hidden`}>
      {/* Top Header */}
      <div className={`p-4 md:p-6 border-b ${colors.border} bg-white/50 dark:bg-black/20 backdrop-blur-md flex-shrink-0`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate("/exercises")}
              className={`p-2 rounded-xl border ${colors.border} ${colors.bgHover} transition-all`}
            >
              <ArrowLeft className={`w-5 h-5 ${colors.textSecondary}`} />
            </button>
            <div>
              <div className="flex items-center space-x-3 mb-1">
                <h1 className={`text-2xl font-black tracking-tight ${colors.textPrimary}`}>
                  {exercise.name}
                </h1>
                {getStatusBadge(exercise.reviewStatus)}
              </div>
              <div className="flex items-center space-x-4 text-sm font-medium">
                <span className={`flex items-center ${colors.textTertiary}`}>
                  <Activity className="w-4 h-4 mr-1.5 opacity-60" />
                  {exercise.category || "Genel"}
                </span>
                <span className={`w-1 h-1 rounded-full bg-zinc-300 dark:bg-zinc-700`} />
                <span className={`flex items-center ${getDifficultyColor(exercise.difficulty)} lowercase`}>
                  {exercise.difficulty || "beginner"}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setIsEditModalOpen(true)}
              className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all font-bold shadow-lg shadow-blue-500/20 text-sm"
            >
              <Edit className="w-4 h-4 mr-2" />
              {t("exerciseDetails.edit")}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          
          {/* Top Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`p-6 rounded-2xl bg-white dark:bg-zinc-900 border ${colors.cardBorder} shadow-sm group hover:shadow-md transition-all`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-500">
                  <Target className="w-5 h-5" />
                </div>
                <h3 className={`font-bold ${colors.textPrimary}`}>{t("exerciseDetails.targetMuscles")}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {exercise.bodyParts && exercise.bodyParts.length > 0 ? (
                  exercise.bodyParts.map((part, i) => (
                    <span key={i} className="px-3 py-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-xs font-bold text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700">
                      {part}
                    </span>
                  ))
                ) : (
                  <span className={`text-sm ${colors.textMuted}`}>{t("exerciseDetails.noMuscles")}</span>
                )}
              </div>
            </div>

            <div className={`p-6 rounded-2xl bg-white dark:bg-zinc-900 border ${colors.cardBorder} shadow-sm group hover:shadow-md transition-all`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                  <Settings className="w-5 h-5" />
                </div>
                <h3 className={`font-bold ${colors.textPrimary}`}>{t("exerciseDetails.equipment")}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {exercise.equipment && exercise.equipment.length > 0 ? (
                  exercise.equipment.map((item, i) => (
                    <span key={i} className="px-3 py-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-xs font-bold text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700">
                      {item}
                    </span>
                  ))
                ) : (
                  <span className={`text-sm ${colors.textMuted}`}>{t("exerciseDetails.noEquipment")}</span>
                )}
              </div>
            </div>

            <div className={`p-6 rounded-2xl bg-white dark:bg-zinc-900 border ${colors.cardBorder} shadow-sm group hover:shadow-md transition-all`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                  <Layers className="w-5 h-5" />
                </div>
                <h3 className={`font-bold ${colors.textPrimary}`}>{t("exerciseDetails.parameters")}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${colors.textMuted} mb-1`}>{t("exerciseDetails.setRep")}</div>
                  <div className={`text-lg font-black ${colors.textPrimary}`}>
                    {exercise.sets || "—"} x {exercise.reps || "—"}
                  </div>
                </div>
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${colors.textMuted} mb-1`}>{t("exerciseDetails.rest")}</div>
                  <div className={`text-lg font-black ${colors.textPrimary}`}>
                    {exercise.restTime ? `${exercise.restTime} sn` : "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Media & Instructions Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Media Gallery */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 px-1">
                <Play className="w-5 h-5 text-blue-500" />
                <h2 className={`text-lg font-black ${colors.textPrimary}`}>{t("exerciseDetails.mediaGallery")}</h2>
              </div>
              
              <div className={`aspect-video rounded-3xl overflow-hidden bg-zinc-100 dark:bg-zinc-900 border-4 border-white dark:border-zinc-800 shadow-xl flex items-center justify-center group relative cursor-pointer`}>
                {exercise.mediaUrls && exercise.mediaUrls.length > 0 ? (
                  <img 
                    src={exercise.mediaUrls[0]} 
                    alt={exercise.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                  />
                ) : (
                  <div className="text-center">
                    <Dumbbell className="w-12 h-12 text-zinc-300 dark:text-zinc-700 mx-auto mb-2" />
                    <span className={`text-sm font-medium ${colors.textMuted}`}>{t("exerciseDetails.noMedia")}</span>
                  </div>
                )}
                {exercise.mediaUrls && exercise.mediaUrls.length > 1 && (
                  <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/60 backdrop-blur-md rounded-lg text-[10px] text-white font-bold border border-white/20">
                    +{exercise.mediaUrls.length - 1} Görsel Daha
                  </div>
                )}
              </div>

              {exercise.mediaUrls && exercise.mediaUrls.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {exercise.mediaUrls.slice(1, 5).map((url, i) => (
                    <div key={i} className="aspect-square rounded-xl overflow-hidden border-2 border-white dark:border-zinc-800 shadow-md">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 px-1">
                <FileText className="w-5 h-5 text-orange-500" />
                <h2 className={`text-lg font-black ${colors.textPrimary}`}>{t("exerciseDetails.instructions")}</h2>
              </div>
              
              <div className={`p-5 sm:p-8 rounded-3xl bg-white dark:bg-zinc-900 border ${colors.cardBorder} shadow-sm min-h-[200px] sm:min-h-[300px]`}>
                {exercise.instructions ? (
                  <div className={`prose dark:prose-invert prose-sm max-w-none text-sm leading-relaxed ${colors.textSecondary}`}>
                    {exercise.instructions.split('\n').map((line, i) => (
                      <p key={i} className="mb-4">{line}</p>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-12">
                    <Info className="w-8 h-8 text-zinc-300 dark:text-zinc-700 mb-3" />
                    <p className={`text-sm ${colors.textMuted}`}>{t("exerciseDetails.noInstructions")}</p>
                  </div>
                )}
                
                {exercise.notes && (
                  <div className="mt-8 pt-6 border-t border-zinc-100 dark:border-zinc-800">
                    <h4 className={`text-xs font-black uppercase tracking-widest ${colors.textMuted} mb-3`}>{t("exerciseDetails.notes")}</h4>
                    <p className={`text-sm ${colors.textTertiary} leading-relaxed bg-amber-50/50 dark:bg-amber-500/5 p-4 rounded-xl border border-amber-500/10`}>
                      {exercise.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Metadata Footer */}
          <div className={`mt-12 pt-8 border-t ${colors.border} grid grid-cols-1 md:grid-cols-2 gap-4 pb-8`}>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                <User className={`w-5 h-5 ${colors.textTertiary}`} />
              </div>
              <div>
                <div className={`text-[10px] font-black uppercase tracking-widest ${colors.textMuted}`}>{t("exerciseDetails.creator")}</div>
                <div className={`text-sm font-bold ${colors.textSecondary}`}>
                  {exercise.updatedByName || exercise.createdBy || "Sistem"}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 md:justify-end">
              <div className="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                <Calendar className={`w-5 h-5 ${colors.textTertiary}`} />
              </div>
              <div className="md:text-right">
                <div className={`text-[10px] font-black uppercase tracking-widest ${colors.textMuted}`}>{t("exerciseDetails.createdAt")}</div>
                <div className={`text-sm font-bold ${colors.textSecondary}`}>
                  {exercise.createdAt ? new Date(exercise.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <ExerciseModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdate}
        exercise={exercise}
        title="Egzersizi Düzenle"
        loading={false}
      />
    </div>
  );
};

export default ExerciseDetailsPage;
